import { getCountryLabel } from './countryRules'

type LocationSource = {
  city?: unknown
  suburb?: unknown
  location?: unknown
  state?: unknown
  region?: unknown
  locationState?: unknown
  countryCode?: unknown
  country?: unknown
  address?: {
    city?: unknown
    state?: unknown
    countryCode?: unknown
    country?: unknown
  } | null
}

const COUNTRY_CODE_BY_NAME: Record<string, string> = {
  AU: 'AU',
  AUSTRALIA: 'AU',
  NZ: 'NZ',
  'NEW ZEALAND': 'NZ',
  US: 'US',
  USA: 'US',
  'UNITED STATES': 'US',
  'UNITED STATES OF AMERICA': 'US',
  CA: 'CA',
  CANADA: 'CA',
  GB: 'GB',
  UK: 'GB',
  'UNITED KINGDOM': 'GB',
  IE: 'IE',
  IRELAND: 'IE',
  SG: 'SG',
  SINGAPORE: 'SG',
  HK: 'HK',
  'HONG KONG': 'HK',
}

const AU_REGION_CODES = new Set(['ACT', 'NSW', 'NT', 'QLD', 'SA', 'TAS', 'VIC', 'WA'])

const clean = (value: unknown): string => String(value || '').trim()

const toCountryCode = (value: unknown): string | null => {
  const raw = clean(value)
  if (!raw) return null
  const key = raw.toUpperCase()
  return COUNTRY_CODE_BY_NAME[key] || null
}

const normalizeCity = (value: unknown): string | null => {
  const raw = clean(value)
  if (!raw) return null
  return raw
    .split(/\s+/)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

const normalizeState = (value: unknown): string | null => {
  const raw = clean(value)
  if (!raw) return null
  if (/^[A-Za-z]{2,3}$/.test(raw)) return raw.toUpperCase()
  return raw
}

const sameText = (a: unknown, b: unknown) => clean(a).toLowerCase() === clean(b).toLowerCase()

const dedupeParts = (parts: Array<unknown>): string[] => {
  const seen = new Set<string>()
  const out: string[] = []
  for (const part of parts) {
    const value = clean(part)
    if (!value) continue
    const key = value.toLowerCase()
    if (seen.has(key)) continue
    seen.add(key)
    out.push(value)
  }
  return out
}

const splitLocationValue = (value: unknown): string[] =>
  clean(value)
    .split(',')
    .map((part) => part.trim())
    .filter(Boolean)

const sanitizeStateByCountry = (state: string | null, countryCode: string | null): string | null => {
  if (!state) return null
  const token = state.toUpperCase()
  if (AU_REGION_CODES.has(token) && countryCode && countryCode !== 'AU') return null
  return state
}

export const buildProviderLocation = (source: LocationSource) => {
  const locationParts = splitLocationValue(source.location)
  const locationCountryCandidate = locationParts.length >= 3 ? locationParts[locationParts.length - 1] : null
  const cityCandidate = source.city || source.suburb || source.address?.city || locationParts[0] || null
  const stateCandidate = source.state || source.region || source.locationState || source.address?.state || locationParts[1] || null
  const rawCountryCode = source.countryCode || source.address?.countryCode || null
  const countryCode =
    toCountryCode(rawCountryCode) ||
    toCountryCode(source.country) ||
    toCountryCode(source.address?.country) ||
    toCountryCode(locationCountryCandidate) ||
    null
  const explicitCountry = clean(source.country) || clean(source.address?.country) || clean(locationCountryCandidate)
  const country = explicitCountry || (countryCode ? getCountryLabel(countryCode as any) : '')
  const city = normalizeCity(cityCandidate)
  let state = normalizeState(stateCandidate)

  if (country && sameText(state, country)) state = null
  state = sanitizeStateByCountry(state, countryCode)

  const displayParts = dedupeParts([city, state, country])
  const display = displayParts.join(', ') || null

  return {
    city,
    state,
    country: country || null,
    countryCode,
    display,
  }
}
